import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { ActiveResouces, DBLocation, ResourceKind } from "src/types";
import { RecurringSchedule, RecurringScheduleLocationEntry } from "../types";

type ResourceOption = {
  id: string;
  label: string;
  kind: Exclude<ResourceKind, "none">;
};

type LocationDraft = Omit<RecurringScheduleLocationEntry, "billableAmount"> & {
  selected: boolean;
  billableAmount: string;
};

interface RecurringScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (schedule: RecurringSchedule) => void;
  locations: DBLocation[];
  activeResources: ActiveResouces;
  clientId: string;
  initialSchedule?: RecurringSchedule;
}

const frequencyOptions: Array<{ value: RecurringSchedule["frequency"]; label: string }> = [
  { value: "one_time", label: "One Time" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly (every 3 months)" },
  { value: "semiannual", label: "Semi-Annually (every 6 months)" },
  { value: "annual", label: "Annually" },
];

const buildDefaultDraftLocations = (
  locations: DBLocation[],
  defaultResourceKind: ResourceKind,
  defaultResourceId: string,
  defaultScheduledDate: string,
): LocationDraft[] =>
  locations
    .filter((location) => !!location.id)
    .map((location) => ({
      locationId: String(location.id),
      locationLabel: location.label || location.address || String(location.id),
      selected: true,
      resourceKind: defaultResourceKind,
      resourceId: defaultResourceId,
      scheduledDate: defaultScheduledDate,
      billableAmount: "",
    }));

const toDateInputValue = (value?: string) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const getInitialState = (locations: DBLocation[], initialSchedule?: RecurringSchedule) => {
  const defaults = buildDefaultDraftLocations(
    locations,
    initialSchedule?.defaultResourceKind || "none",
    initialSchedule?.defaultResourceId || "",
    toDateInputValue(initialSchedule?.defaultScheduledDate),
  );

  if (!initialSchedule) {
    return {
      frequency: "biweekly" as RecurringSchedule["frequency"],
      startDate: "",
      endDate: "",
      isBillable: false,
      locationStrategy: "same_resource" as RecurringSchedule["locationStrategy"],
      dateStrategy: "same_date" as RecurringSchedule["dateStrategy"],
      defaultResourceKind: "none" as ResourceKind,
      defaultResourceId: "",
      defaultScheduledDate: "",
      locationDrafts: defaults,
    };
  }

  const selectedByLocation = new Map(initialSchedule.locations.map((entry) => [entry.locationId, entry]));

  return {
    frequency: initialSchedule.frequency,
    startDate: toDateInputValue(initialSchedule.startDate),
    endDate: toDateInputValue(initialSchedule.endDate),
    isBillable:
      !!initialSchedule.isBillable || initialSchedule.locations.some((entry) => Number(entry.billableAmount) > 0),
    locationStrategy: initialSchedule.locationStrategy,
    dateStrategy: initialSchedule.dateStrategy,
    defaultResourceKind: initialSchedule.defaultResourceKind,
    defaultResourceId: initialSchedule.defaultResourceId,
    defaultScheduledDate: toDateInputValue(initialSchedule.defaultScheduledDate),
    locationDrafts: defaults.map((draft) => {
      const existing = selectedByLocation.get(draft.locationId);
      if (!existing) return { ...draft, selected: false };
      return {
        ...draft,
        selected: true,
        resourceKind: existing.resourceKind,
        resourceId: existing.resourceId,
        scheduledDate: toDateInputValue(existing.scheduledDate),
        billableAmount: existing.billableAmount == null ? "" : String(existing.billableAmount),
      };
    }),
  };
};

export const RecurringScheduleModal = ({
  isOpen,
  onClose,
  onSave,
  locations,
  activeResources,
  clientId,
  initialSchedule,
}: RecurringScheduleModalProps) => {
  const [frequency, setFrequency] = useState<RecurringSchedule["frequency"]>("biweekly");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isBillable, setIsBillable] = useState(false);
  const [locationStrategy, setLocationStrategy] = useState<RecurringSchedule["locationStrategy"]>("same_resource");
  const [dateStrategy, setDateStrategy] = useState<RecurringSchedule["dateStrategy"]>("same_date");
  const [defaultResourceKind, setDefaultResourceKind] = useState<ResourceKind>("none");
  const [defaultResourceId, setDefaultResourceId] = useState("");
  const [defaultScheduledDate, setDefaultScheduledDate] = useState("");
  const [locationDrafts, setLocationDrafts] = useState<LocationDraft[]>([]);
  const [error, setError] = useState("");

  const teamResourceOptions = useMemo<ResourceOption[]>(
    () =>
      (activeResources.employees || []).map((employee) => ({
        id: employee.id,
        label: employee.displayName || employee.email || employee.id,
        kind: "team_member",
      })),
    [activeResources.employees],
  );

  const vendorResourceOptions = useMemo<ResourceOption[]>(
    () =>
      (activeResources.vendors || [])
        .filter((vendor) => vendor.clients?.includes(clientId))
        .map((vendor) => ({
          id: vendor.id,
          label: vendor.displayName || vendor.vendorId || vendor.id,
          kind: "vendor",
        })),
    [activeResources.vendors, clientId],
  );

  const allResourceOptions = useMemo(
    () => ({
      team_member: teamResourceOptions,
      vendor: vendorResourceOptions,
    }),
    [teamResourceOptions, vendorResourceOptions],
  );

  useEffect(() => {
    if (!isOpen) return;

    const initial = getInitialState(locations, initialSchedule);
    setFrequency(initial.frequency);
    setStartDate(initial.startDate);
    setEndDate(initial.endDate);
    setIsBillable(initial.isBillable);
    setLocationStrategy(initial.locationStrategy);
    setDateStrategy(initial.dateStrategy);
    setDefaultResourceKind(initial.defaultResourceKind);
    setDefaultResourceId(initial.defaultResourceId);
    setDefaultScheduledDate(initial.defaultScheduledDate);
    setLocationDrafts(initial.locationDrafts);
    setError("");
  }, [isOpen, locations, initialSchedule]);

  useEffect(() => {
    if (!isOpen || locationStrategy !== "same_resource") return;
    setLocationDrafts((prev) =>
      prev.map((draft) => ({
        ...draft,
        resourceKind: defaultResourceKind,
        resourceId: defaultResourceKind === "none" ? "" : defaultResourceId,
      })),
    );
  }, [isOpen, locationStrategy, defaultResourceKind, defaultResourceId]);

  useEffect(() => {
    if (!isOpen || dateStrategy !== "same_date") return;
    setLocationDrafts((prev) => prev.map((draft) => ({ ...draft, scheduledDate: defaultScheduledDate })));
  }, [isOpen, dateStrategy, defaultScheduledDate]);

  const resourceOptionsForKind = defaultResourceKind !== "none" ? allResourceOptions[defaultResourceKind] || [] : [];
  const selectedCount = locationDrafts.filter((draft) => draft.selected).length;
  const hasAnyLocationSelected = selectedCount > 0;
  const areAllLocationsSelected = locationDrafts.length > 0 && locationDrafts.every((draft) => draft.selected);

  const setAllLocationsSelected = (selected: boolean) => {
    setLocationDrafts((prev) => prev.map((draft) => ({ ...draft, selected })));
  };

  const close = () => {
    setError("");
    onClose();
  };

  const save = () => {
    if (!startDate || !endDate) {
      setError("Select the recurring schedule date range.");
      return;
    }

    if (new Date(endDate).getTime() < new Date(startDate).getTime()) {
      setError("End date must be after start date.");
      return;
    }

    const selectedLocations = locationDrafts.filter((draft) => draft.selected);
    if (!selectedLocations.length) {
      setError("Select at least one location.");
      return;
    }

    if (selectedLocations.some((draft) => !draft.scheduledDate)) {
      setError("Each selected location needs a scheduled date.");
      return;
    }

    if (isBillable) {
      const hasInvalidBillableAmounts = selectedLocations.some(
        (draft) => !draft.billableAmount || Number(draft.billableAmount) <= 0,
      );

      if (hasInvalidBillableAmounts) {
        setError("Enter a billable amount for each selected location.");
        return;
      }
    }

    onSave({
      frequency,
      startDate,
      endDate,
      isBillable,
      locationStrategy,
      dateStrategy,
      defaultResourceKind,
      defaultResourceId,
      defaultScheduledDate,
      locations: selectedLocations.map((draft) => ({
        locationId: draft.locationId,
        locationLabel: draft.locationLabel,
        scheduledDate: draft.scheduledDate,
        resourceKind: draft.resourceKind,
        resourceId: draft.resourceId,
        billableAmount: isBillable ? Number(draft.billableAmount) : undefined,
      })),
    });
    close();
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={close} />
      <Modal>
        <Header>
          <Title>Recurring Service Schedule</Title>
          <CloseButton type="button" onClick={close}>
            x
          </CloseButton>
        </Header>

        <Body>
          <Section>
            <SectionTitle>Cycle Setup</SectionTitle>
            <FieldsRow>
              <Field>
                <FieldLabel>Frequency *</FieldLabel>
                <FieldSelect
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as RecurringSchedule["frequency"])}
                >
                  {frequencyOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </FieldSelect>
              </Field>
              <Field>
                <FieldLabel>Start Date *</FieldLabel>
                <FieldInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel>End Date *</FieldLabel>
                <FieldInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Field>
            </FieldsRow>
          </Section>

          <Section>
            <SectionTitle>Billing</SectionTitle>
            <FieldsRow $columns={1}>
              <Field>
                <FieldLabel>Billable</FieldLabel>
                <BillableToggleRow>
                  <BillableToggleButton type="button" $isActive={!isBillable} onClick={() => setIsBillable(false)}>
                    Not Billable
                  </BillableToggleButton>
                  <BillableToggleButton type="button" $isActive={isBillable} onClick={() => setIsBillable(true)}>
                    Billable
                  </BillableToggleButton>
                </BillableToggleRow>
              </Field>
            </FieldsRow>
          </Section>

          <Section>
            <SectionTitle>Default Assignment</SectionTitle>
            <FieldsRow>
              <Field>
                <FieldLabel>Resource Assignment Type</FieldLabel>
                <FieldSelect
                  value={locationStrategy}
                  onChange={(e) => setLocationStrategy(e.target.value as RecurringSchedule["locationStrategy"])}
                >
                  <option value="same_resource">One resource for all selected locations</option>
                  <option value="custom_resource">Different resource per location</option>
                </FieldSelect>
              </Field>
              <Field>
                <FieldLabel>Resource Type</FieldLabel>
                <FieldSelect
                  value={defaultResourceKind}
                  onChange={(e) => {
                    const kind = e.target.value as ResourceKind;
                    setDefaultResourceKind(kind);
                    setDefaultResourceId("");
                  }}
                  disabled={locationStrategy !== "same_resource"}
                >
                  <option value="none">Unassigned</option>
                  <option value="team_member">Team Member</option>
                  <option value="vendor">Vendor</option>
                </FieldSelect>
              </Field>
              <Field>
                <FieldLabel>Resource</FieldLabel>
                <FieldSelect
                  value={defaultResourceId}
                  onChange={(e) => setDefaultResourceId(e.target.value)}
                  disabled={locationStrategy !== "same_resource" || defaultResourceKind === "none"}
                >
                  <option value="">Select resource...</option>
                  {resourceOptionsForKind.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </FieldSelect>
              </Field>
            </FieldsRow>

            <FieldsRow $columns={3}>
              <Field>
                <FieldLabel>Start Date Type</FieldLabel>
                <FieldSelect
                  value={dateStrategy}
                  onChange={(e) => setDateStrategy(e.target.value as RecurringSchedule["dateStrategy"])}
                >
                  <option value="same_date">One date for all selected locations</option>
                  <option value="custom_date">Different date per location</option>
                </FieldSelect>
              </Field>

              <Field>
                <FieldLabel>Start Date</FieldLabel>
                <FieldInput
                  type="date"
                  value={defaultScheduledDate}
                  onChange={(e) => setDefaultScheduledDate(e.target.value)}
                  disabled={dateStrategy !== "same_date"}
                />
              </Field>
            </FieldsRow>
          </Section>

          <Section>
            <SectionTitle>Location Schedule ({selectedCount} selected)</SectionTitle>
            <SelectionToolbar>
              <SelectionCheckbox>
                <input
                  type="checkbox"
                  checked={areAllLocationsSelected}
                  onChange={(e) => setAllLocationsSelected(e.target.checked)}
                  disabled={!locationDrafts.length}
                />
                <span>Select all locations</span>
              </SelectionCheckbox>
              <SelectionActions>
                <SelectionActionButton
                  type="button"
                  onClick={() => setAllLocationsSelected(true)}
                  disabled={!locationDrafts.length || areAllLocationsSelected}
                >
                  Select all
                </SelectionActionButton>
                <SelectionActionButton
                  type="button"
                  onClick={() => setAllLocationsSelected(false)}
                  disabled={!locationDrafts.length || !hasAnyLocationSelected}
                >
                  Clear all
                </SelectionActionButton>
              </SelectionActions>
            </SelectionToolbar>
            <LocationTable>
              <LocationHeaderRow>
                <HeaderCell style={{ width: "90px" }}>Include</HeaderCell>
                <HeaderCell>Location</HeaderCell>
                <HeaderCell style={{ width: "180px" }}>Resource Type</HeaderCell>
                <HeaderCell style={{ width: "240px" }}>Resource</HeaderCell>
                <HeaderCell style={{ width: "180px" }}>Scheduled Date</HeaderCell>
                {isBillable ? <HeaderCell style={{ width: "180px" }}>Billable Amount</HeaderCell> : null}
              </LocationHeaderRow>

              {locationDrafts.length === 0 && <EmptyState>No locations found for this client.</EmptyState>}

              {locationDrafts.map((draft) => {
                const rowResourceOptions =
                  draft.resourceKind === "none" ? [] : allResourceOptions[draft.resourceKind] || [];
                return (
                  <LocationRow key={draft.locationId}>
                    <BodyCell style={{ width: "90px" }}>
                      <input
                        type="checkbox"
                        checked={draft.selected}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setLocationDrafts((prev) =>
                            prev.map((row) =>
                              row.locationId === draft.locationId ? { ...row, selected: checked } : row,
                            ),
                          );
                        }}
                      />
                    </BodyCell>
                    <BodyCell>{draft.locationLabel}</BodyCell>
                    <BodyCell style={{ width: "180px" }}>
                      <FieldSelect
                        value={draft.resourceKind}
                        disabled={locationStrategy === "same_resource" || !draft.selected}
                        onChange={(e) => {
                          const nextKind = e.target.value as ResourceKind;
                          setLocationDrafts((prev) =>
                            prev.map((row) =>
                              row.locationId === draft.locationId
                                ? { ...row, resourceKind: nextKind, resourceId: "" }
                                : row,
                            ),
                          );
                        }}
                      >
                        <option value="none">Unassigned</option>
                        <option value="team_member">Team Member</option>
                        <option value="vendor">Vendor</option>
                      </FieldSelect>
                    </BodyCell>
                    <BodyCell style={{ width: "240px" }}>
                      <FieldSelect
                        value={draft.resourceId}
                        disabled={
                          locationStrategy === "same_resource" || draft.resourceKind === "none" || !draft.selected
                        }
                        onChange={(e) => {
                          const nextResource = e.target.value;
                          setLocationDrafts((prev) =>
                            prev.map((row) =>
                              row.locationId === draft.locationId ? { ...row, resourceId: nextResource } : row,
                            ),
                          );
                        }}
                      >
                        <option value="">Select resource...</option>
                        {rowResourceOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </FieldSelect>
                    </BodyCell>
                    <BodyCell style={{ width: "180px" }}>
                      <FieldInput
                        type="date"
                        value={draft.scheduledDate}
                        disabled={dateStrategy === "same_date" || !draft.selected}
                        onChange={(e) => {
                          const nextDate = e.target.value;
                          setLocationDrafts((prev) =>
                            prev.map((row) =>
                              row.locationId === draft.locationId ? { ...row, scheduledDate: nextDate } : row,
                            ),
                          );
                        }}
                      />
                    </BodyCell>
                    {isBillable ? (
                      <BodyCell style={{ width: "180px" }}>
                        <FieldInput
                          type="number"
                          min={0}
                          step="0.01"
                          placeholder="0.00"
                          value={draft.billableAmount}
                          disabled={!draft.selected}
                          onChange={(e) => {
                            const nextAmount = e.target.value;
                            setLocationDrafts((prev) =>
                              prev.map((row) =>
                                row.locationId === draft.locationId ? { ...row, billableAmount: nextAmount } : row,
                              ),
                            );
                          }}
                        />
                      </BodyCell>
                    ) : null}
                  </LocationRow>
                );
              })}
            </LocationTable>
          </Section>

          {!!error && <ErrorText>{error}</ErrorText>}
        </Body>

        <Footer>
          <SecondaryButton type="button" onClick={close}>
            Cancel
          </SecondaryButton>
          <PrimaryButton type="button" onClick={save} disabled={!locations.length}>
            Save Schedule
          </PrimaryButton>
        </Footer>
      </Modal>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 10002;
`;

const Modal = styled.div`
  position: fixed;
  inset: 4vh 4vw;
  background: #ffffff;
  border: 1px solid #dde2e7;
  z-index: 10003;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid #dde2e7;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  color: #1f2a37;
  font-size: 16px;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: #6f7782;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 16px 18px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Section = styled.section`
  border: 1px solid #dde2e7;
  padding: 14px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px;
  color: #1f2a37;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const FieldsRow = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div``;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  color: #1f2a37;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

const FieldSelect = styled.select`
  width: 100%;
  border: 1px solid #c9d2dc;
  background: #ffffff;
  padding: 10px 12px;
  font-size: 13px;
`;

const FieldInput = styled.input`
  width: 100%;
  border: 1px solid #c9d2dc;
  background: #ffffff;
  padding: 10px 12px;
  font-size: 13px;
`;

const BillableToggleRow = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const BillableToggleButton = styled.button<{ $isActive: boolean }>`
  border: 1px solid ${({ $isActive }) => ($isActive ? "#1f2a37" : "#c9d2dc")};
  background: ${({ $isActive }) => ($isActive ? "#1f2a37" : "#ffffff")};
  color: ${({ $isActive }) => ($isActive ? "#ffffff" : "#1f2a37")};
  padding: 8px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

const SelectionToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SelectionCheckbox = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #1f2a37;
  user-select: none;

  input {
    width: 14px;
    height: 14px;
  }
`;

const SelectionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const SelectionActionButton = styled.button`
  border: 1px solid #c9d2dc;
  background: #ffffff;
  color: #1f2a37;
  padding: 6px 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;

  &:disabled {
    color: #8f97a1;
    border-color: #dde2e7;
    cursor: not-allowed;
  }
`;

const LocationTable = styled.div`
  border: 1px solid #dde2e7;
`;

const LocationHeaderRow = styled.div`
  display: flex;
  border-bottom: 1px solid #dde2e7;
  background: #f9fafb;
`;

const HeaderCell = styled.div`
  flex: 1;
  padding: 10px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6f7782;
  font-weight: 600;
`;

const LocationRow = styled.div`
  display: flex;
  border-top: 1px solid #eef1f4;
`;

const BodyCell = styled.div`
  flex: 1;
  padding: 10px 12px;
  font-size: 13px;
  color: #1f2a37;
  display: flex;
  align-items: center;
`;

const EmptyState = styled.div`
  padding: 16px;
  color: #6f7782;
  font-size: 13px;
`;

const ErrorText = styled.p`
  margin: 0;
  color: #cc3434;
  font-size: 13px;
`;

const Footer = styled.div`
  padding: 12px 18px;
  border-top: 1px solid #dde2e7;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const SecondaryButton = styled.button`
  border: 1px solid #c9d2dc;
  background: #ffffff;
  color: #1f2a37;
  padding: 9px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;
`;

const PrimaryButton = styled.button`
  border: 1px solid #1f2a37;
  background: #1f2a37;
  color: #ffffff;
  padding: 9px 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;

  &:disabled {
    border-color: #c9d2dc;
    background: #c9d2dc;
    cursor: not-allowed;
  }
`;
